/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from "@angular/cdk/scrolling";
import { NgTemplateOutlet } from "@angular/common";
import {
  AfterContentInit,
  afterRenderEffect,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChildren,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  model,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
  signal,
  TemplateRef,
  TrackByFunction,
  viewChild,
  ViewEncapsulation,
} from "@angular/core";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import {
  BlockableUI,
  PrimeNGConfig,
  PrimeTemplate,
  SharedModule,
  TranslationKeys,
  TreeDragDropService,
  TreeNode,
} from "primeng/api";
import { ScrollerModule } from "primeng/scroller";
import { Subscription } from "rxjs";
import { TreeNodeComponent } from "src/prime/tree/tree-node.component";
import { getNodesBetween } from "src/prime/tree/utils/getNodesBetween";
import {
  serializeNodes,
  type SerializedNode,
} from "src/prime/tree/utils/serializeNodes";
import { CdkPerItemSizeVirtualScrollModule } from "src/standard/cdk/per-item-size-virtual-scroll";
import { StandardIconComponent } from "src/standard/standard-icon/standard-icon.component";
import { hasClass } from "src/standard/ts/dom/hasClass";
import invariant from "tiny-invariant";
import {
  TreeNodeCollapseEvent,
  TreeNodeContextMenuSelectEvent,
  TreeNodeDropEvent,
  TreeNodeExpandEvent,
  TreeNodeSelectEvent,
  TreeNodeUnSelectEvent,
} from "./tree.interface";
export type { SerializedNode };

export interface IContextMenu {
  show: (event: Event) => void;
  containerViewChild?: {
    nativeElement: HTMLElement;
  };
}

/**
 * Tree is used to display hierarchical data.
 *
 * ## Sticky Parent Nodes
 * When using virtual scrolling, you can enable sticky parent nodes by setting `stickyParentNodes="true"`.
 * This feature keeps parent nodes visible at the top of the tree when scrolling through their children,
 * providing better context and navigation.
 *
 * Example:
 * ```html
 * <p-tree
 *   [value]="nodes"
 *   [virtualScrollItemSize]="{ parent: 40, child: 30 }"
 *   [stickyParentNodes]="true"
 *   [trackBy]="trackByFn">
 * </p-tree>
 * ```
 *
 * @group Components
 */
@Component({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "p-tree",
  templateUrl: "./tree.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrls: ["./tree.component.css"],
  host: {
    class: "p-element",
  },
  imports: [
    NgTemplateOutlet,
    ScrollingModule,
    CdkPerItemSizeVirtualScrollModule,
    TreeNodeComponent,
    StandardIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeComponent<
    TData,
    TVirtualScrollItemSize extends
      | number
      | { parent: number; child: number }
      | undefined = number | { parent: number; child: number } | undefined,
  >
  implements OnInit, AfterContentInit, OnDestroy, BlockableUI
{
  el = inject<ElementRef<HTMLElement>>(ElementRef);
  dragDropService = inject(TreeDragDropService, { optional: true });
  config = inject(PrimeNGConfig);
  private cd = inject(ChangeDetectorRef);

  readonly faCircleNotch = faCircleNotch;

  /**
   * An array of treenodes.
   * @group Props
   */
  readonly value = model.required<TreeNode<TData>[]>();
  /**
   * Defines the selection mode.
   * @group Props
   */
  readonly selectionMode = input<"single" | "multiple" | "checkbox">();

  readonly touchSelectionMode = input<"single" | "multiple">("multiple");
  /**
   * Loading mode display.
   * @group Props
   */
  readonly loadingMode = input<"mask" | "icon">("mask");
  /**
   * A single treenode instance or an array to refer to the selections.
   * @group Props
   */
  readonly selection = model<TreeNode<TData> | readonly TreeNode<TData>[]>();
  /**
   * Inline style of the component.
   * @group Props
   */
  readonly style = input<string>();
  /**
   * Style class of the component.
   * @group Props
   */
  readonly styleClass = input<string>();
  /**
   * Context menu instance.
   * @group Props
   */
  readonly contextMenu = input<IContextMenu>();
  /**
   * Scope of the draggable nodes to match a droppableScope.
   * @group Props
   */
  readonly draggableScope = input<unknown>();
  /**
   * Scope of the droppable nodes to match a draggableScope.
   * @group Props
   */
  readonly droppableScope = input<unknown>();
  /**
   * Whether the nodes are draggable.
   * @group Props
   */
  readonly draggableNodes = input<boolean, unknown>(undefined, {
    transform: booleanAttribute,
  });
  /**
   * Whether the nodes are droppable.
   * @group Props
   */
  readonly droppableNodes = input<boolean, unknown>(undefined, {
    transform: booleanAttribute,
  });
  /**
   * Defines how multiple items can be selected, when true metaKey needs to be pressed to select or unselect an item and when set to false selection of each item can be toggled individually. On touch enabled devices, metaKeySelection is turned off automatically.
   * @group Props
   */
  readonly metaKeySelection = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  /**
   * Whether checkbox selections propagate to ancestor nodes.
   * @group Props
   */
  readonly propagateSelectionUp = input<boolean, unknown>(true, {
    transform: booleanAttribute,
  });
  /**
   * Whether checkbox selections propagate to descendant nodes.
   * @group Props
   */
  readonly propagateSelectionDown = input<boolean, unknown>(true, {
    transform: booleanAttribute,
  });
  /**
   * Displays a loader to indicate data load is in progress.
   * @group Props
   */
  readonly loading = input<boolean, unknown>(undefined, {
    transform: booleanAttribute,
  });
  /**
   * The icon to show while indicating data load is in progress.
   * @group Props
   */
  @Input() loadingIcon: string | undefined;
  /**
   * Text to display when there is no data.
   * @group Props
   */
  readonly emptyMessage = input<string>("");
  /**
   * Used to define a string that labels the tree.
   * @group Props
   */
  readonly ariaLabel = input<string>();
  /**
   * Defines a string that labels the toggler icon for accessibility.
   * @group Props
   */
  readonly togglerAriaLabel = input<string>();
  /**
   * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
   * @group Props
   */
  readonly ariaLabelledBy = input<string>();
  /**
   * When enabled, drop can be accepted or rejected based on condition defined at onNodeDrop.
   * @group Props
   */
  readonly validateDrop = input<boolean, unknown>(undefined, {
    transform: booleanAttribute,
  });

  /**
   * When enabled, allows dragging and dropping of external data.
   * @group Props
   */
  isExternalDataDragDropEnabled = input<boolean>(false);

  /**
   * Function to determine if a node can have children/drop points.
   * @group Props
   */
  readonly canShowDropPointBetweenFn = input<
    ((node: TreeNode<TData>) => boolean) | undefined
  >();
  /**
   * Height of the scrollable viewport.
   * @group Props
   */
  readonly scrollHeight = input<string>("flex");
  /**
   * Whether the data should be loaded on demand during scroll.
   * @group Props
   */
  readonly virtualScroll = computed(() => !!this.virtualScrollItemSize());
  /**
   * Height of an item in the list for VirtualScrolling.
   * @group Props
   */
  readonly virtualScrollItemSize = input<TVirtualScrollItemSize>();
  /**
   * Whether parent nodes should stick to the top when scrolling in virtual scroll mode.
   * @group Props
   */
  readonly stickyParentNodes = input<boolean>();
  readonly _stickyParentItemSize = input<number | undefined>(undefined, {
    alias: "stickyParentItemSize",
  });
  readonly stickyParentItemSize = computed(() => {
    return this._stickyParentItemSize() ?? this.parentItemSize();
  });
  readonly parentItemSize = computed<number | undefined>(() => {
    const virtualScrollItemSize = this.virtualScrollItemSize();
    if (typeof virtualScrollItemSize === "object") {
      return virtualScrollItemSize.parent;
    }

    return virtualScrollItemSize;
  });
  readonly childItemSize = computed<number | undefined>(() => {
    const virtualScrollItemSize = this.virtualScrollItemSize();
    if (typeof virtualScrollItemSize === "object") {
      return virtualScrollItemSize.child;
    }

    return virtualScrollItemSize;
  });
  /**
   * Indentation factor for spacing of the nested node when virtual scrolling is enabled.
   * @group Props
   */
  readonly indentation = input<number | undefined>(1.5);
  /**
   * Custom templates of the component.
   * @group Props
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly _templateMap = signal<Record<string, TemplateRef<any>>>({});
  /**
   * Function to optimize the node list rendering, default algorithm checks for object identity.
   * @group Props
   */
  readonly _trackBy = input.required<
    TrackByFunction<
      TVirtualScrollItemSize extends number | { parent: number; child: number }
        ? SerializedNode<TData>
        : TreeNode<TData>
    >
  >({
    alias: "trackBy",
  });
  readonly virtualTrackBy = (index: number, item: SerializedNode<TData>) => {
    invariant(this.virtualScroll());
    return this._trackBy()(index, item) ?? index;
  };
  readonly trackBy = (index: number, item: TreeNode<TData>) => {
    invariant(!this.virtualScroll());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this._trackBy()(index, item as any) ?? index;
  };

  /**
   * Turn off default Tab Index
   */
  @Input() ignoreDefaultTabOrder: boolean | undefined = false;

  /**
   * Callback to invoke on selection change.
   * @param {(TreeNode<TData> | TreeNode<TData>[] | null)} event - Custom selection change event.
   * @group Emits
   */
  @Output() readonly selectionChange = new EventEmitter<
    TreeNode<TData> | TreeNode<TData>[] | null
  >();
  /**
   * Callback to invoke when a node is selected.
   * @param {TreeNodeSelectEvent} event - Node select event.
   * @group Emits
   */
  @Output() readonly onNodeSelect = new EventEmitter<
    TreeNodeSelectEvent<TData>
  >();
  /**
   * Callback to invoke when a node is unselected.
   * @param {TreeNodeUnSelectEvent} event - Node unselect event.
   * @group Emits
   */
  @Output() readonly onNodeUnselect = new EventEmitter<
    TreeNodeUnSelectEvent<TData>
  >();
  /**
   * Callback to invoke when a node is expanded.
   * @param {TreeNodeExpandEvent} event - Node expand event.
   * @group Emits
   */
  @Output() readonly onNodeExpand = new EventEmitter<
    TreeNodeExpandEvent<TData>
  >();
  /**
   * Callback to invoke when a node is collapsed.
   * @param {TreeNodeCollapseEvent} event - Node collapse event.
   * @group Emits
   */
  @Output() readonly onNodeCollapse = new EventEmitter<
    TreeNodeCollapseEvent<TData>
  >();
  /**
   * Callback to invoke when a node is selected with right click.
   * @param {onNodeContextMenuSelect} event - Node context menu select event.
   * @group Emits
   */
  @Output()
  readonly onNodeContextMenuSelect = new EventEmitter<
    TreeNodeContextMenuSelectEvent<TData>
  >();
  /**
   * Callback to invoke when a node is dropped.
   * @param {TreeNodeDropEvent} event - Node drop event.
   * @group Emits
   */
  @Output() readonly onNodeDrop = new EventEmitter<TreeNodeDropEvent<TData>>();
  /**
   * Callback to invoke in virtual scroll mode when scroll position changes.
   * @param {Event} event - Custom scroll event.
   * @group Emits
   */
  @Output() readonly onScroll = new EventEmitter<Event>();
  /**
   * Callback to invoke in virtual scroll mode when scroll position and item's range in view changes.
   * @param {number} newScrollIndex - Scroll index change event.
   * @group Emits
   */
  @Output()
  readonly onScrollIndexChange = new EventEmitter<number>();

  /**
   * Callback to invoke when dragging over a node.
   */
  @Output() readonly onNodeDragOver = new EventEmitter<
    TreeNodeDropEvent<TData>
  >();

  /**
   * Callback to invoke when drag enters a node.
   */
  @Output() readonly onNodeDragEnter = new EventEmitter<
    TreeNodeDropEvent<TData>
  >();

  /**
   * Callback to invoke when drag leaves a node.
   */
  @Output() readonly onNodeDragLeave = new EventEmitter<
    TreeNodeDropEvent<TData>
  >();

  readonly templates = contentChildren(PrimeTemplate);

  readonly scroller = viewChild<CdkVirtualScrollViewport>("scroller");
  readonly wrapperViewChild = viewChild<ElementRef<HTMLElement>>("wrapper");

  readonly serializedValue = signal<SerializedNode<TData>[]>([]);

  /**
   * Current scroll position for tracking sticky nodes
   */
  readonly currentScrollOffset = signal<number>(0);
  readonly currentScrollIndex = signal<number>(0);

  /**
   * Array of parent nodes that should be sticky based on current scroll position
   */
  readonly stickyParents = computed(() => {
    if (!this.stickyParentNodes() || !this.virtualScroll()) {
      return [];
    }

    const scrollIndex = this.currentScrollIndex();
    const scrollOffset = this.currentScrollOffset();
    const serialized = this.serializedValue();

    if (scrollIndex < 0 || scrollIndex >= serialized.length) {
      return [];
    }

    // Get the first visible item
    const currentItem = serialized[scrollIndex];
    if (!currentItem) {
      return [];
    }

    // Find all parent nodes in the hierarchy of the current visible item
    const stickyNodes: SerializedNode<TData>[] = [];
    const parentChain: TreeNode<TData>[] = [];
    let parent: TreeNode<TData> | undefined =
      currentItem.parent ?? currentItem.node;
    while (parent) {
      parentChain.unshift(parent);
      parent = parent.parent;
    }

    // Calculate cumulative heights to determine actual pixel positions
    let cumulativeHeight = 0;
    const itemPositions = new Map<SerializedNode<TData>, number>();

    for (const item of serialized) {
      itemPositions.set(item, cumulativeHeight);
      cumulativeHeight += this.getVirtualItemSize(item);
    }

    // Only include parents that are scrolled out of view (with an early switch)
    for (const [index, parentNode] of parentChain.entries()) {
      const parentSerializedIndex = serialized.findIndex(
        (node) => node.node === parentNode,
      );

      if (parentSerializedIndex < 0 || parentSerializedIndex > scrollIndex) {
        continue;
      }

      const parentSerializedNode = serialized[parentSerializedIndex];
      if (!parentSerializedNode) {
        continue;
      }

      const parentPosition = itemPositions.get(parentSerializedNode) ?? 0;

      // Show parent if it's scrolled out of view (some pixels sooner)
      // But only if we're currently viewing one of its descendants
      const parentItemSize = this.parentItemSize() ?? 0;
      const childItemSize = this.childItemSize() ?? 0;

      if (parentPosition <= scrollOffset + parentItemSize) {
        // Find the next parent node at the same level or higher
        let nextParentIndex: number = -1;
        for (let i = parentSerializedIndex + 1; i < serialized.length; i++) {
          const item = serialized[i]!;
          // Look for a parent node that's not a descendant of the current parent
          if (item.node.children && item.level <= parentSerializedNode.level) {
            nextParentIndex = i;
            break;
          }
        }

        // Check if the next parent is coming up soon (within lookahead distance)
        if (nextParentIndex >= 0) {
          const nextParentItem = serialized[nextParentIndex]!;
          const numChildrenInParent =
            parentSerializedNode.node.children!.length;
          const parentEnd =
            parentPosition +
            parentItemSize +
            numChildrenInParent * childItemSize;
          const lookaheadDistance = parentItemSize * 0.25;

          // If we're near the end of the current parent's children, show the next parent as sticky instead
          if (scrollOffset + lookaheadDistance >= parentEnd) {
            stickyNodes.push({
              ...nextParentItem,
              // Override level to ensure proper indentation for sticky display
              level: index,
            });
          } else {
            // Show the current parent as sticky
            stickyNodes.push({
              ...parentSerializedNode,
              // Override level to ensure proper indentation for sticky display
              level: index,
            });
          }
        } else {
          // No next parent found, show current parent as sticky
          stickyNodes.push({
            ...parentSerializedNode,
            // Override level to ensure proper indentation for sticky display
            level: index,
          });
        }
      }
    }

    return stickyNodes;
  });

  /**
   * Calculate the total height of sticky parent nodes
   */
  readonly stickyHeight = computed(() => {
    if (!this.stickyParentNodes() || !this.virtualScroll()) {
      return 0;
    }

    const stickyNodes = this.stickyParents();
    let totalHeight = 0;

    for (const node of stickyNodes) {
      const itemSize = this.getVirtualItemSize(node);
      if (typeof itemSize === "number") {
        totalHeight += itemSize;
      }
    }

    return totalHeight;
  });

  /**
   * Tracks the anchor node for shift+click range selection
   */
  readonly selectionAnchor = signal<TreeNode<TData> | null>(null);

  readonly headerTemplate = signal<TemplateRef<unknown> | null>(null);
  readonly footerTemplate = signal<TemplateRef<unknown> | null>(null);
  readonly loaderTemplate = signal<TemplateRef<unknown> | null>(null);
  readonly emptyMessageTemplate = signal<TemplateRef<unknown> | null>(null);
  readonly togglerIconTemplate = signal<TemplateRef<unknown> | null>(null);
  readonly checkboxIconTemplate = signal<TemplateRef<unknown> | null>(null);
  readonly loadingIconTemplate = signal<TemplateRef<unknown> | null>(null);

  public nodeTouched: boolean | null | undefined;

  public dragNodeTree: TreeComponent<TData> | null | undefined;
  public dragNode: TreeNode<TData> | null | undefined;
  public dragNodeSubNodes: TreeNode<TData>[] | null | undefined;
  public dragNodeIndex: number | null | undefined;
  public dragNodeScope: unknown;
  public dragHover = signal<boolean | null | undefined>(undefined);
  public dragStartSubscription: Subscription | null | undefined;
  public dragStopSubscription: Subscription | null | undefined;

  constructor() {
    this.getVirtualItemSize = this.getVirtualItemSize.bind(this);

    afterRenderEffect((cleanup) => {
      const scroller = this.scroller();
      if (!scroller) {
        return;
      }

      const sub = scroller.elementScrolled().subscribe((event) => {
        this.onScrollInternal(event);
      });

      cleanup(() => {
        sub.unsubscribe();
      });
    });
  }

  ngOnInit() {
    if (this.droppableNodes()) {
      invariant(this.dragDropService);
      this.dragStartSubscription = this.dragDropService.dragStart$.subscribe(
        (event) => {
          this.dragNodeTree = event.tree;
          this.dragNode = event.node;
          this.dragNodeSubNodes = event.subNodes;
          this.dragNodeIndex = event.index;
          this.dragNodeScope = event.scope;
        },
      );

      this.dragStopSubscription = this.dragDropService.dragStop$.subscribe(
        (event) => {
          this.dragNodeTree = null;
          this.dragNode = null;
          this.dragNodeSubNodes = null;
          this.dragNodeIndex = null;
          this.dragNodeScope = null;
          this.dragHover.set(false);
        },
      );
    }
  }

  onScrollInternal(event: Event) {
    const scroller = this.scroller();
    if (scroller) {
      this.currentScrollOffset.set(scroller.measureScrollOffset());
    }

    this.onScroll.emit(event);
  }

  /**
   * Handles scroll index changes to update sticky parent nodes
   */
  onScrollIndexChangeInternal(newIndex: number) {
    this.currentScrollIndex.set(newIndex);
    this.onScrollIndexChange.emit(newIndex);
  }

  get emptyMessageLabel(): string {
    return (
      this.emptyMessage() ||
      this.config.getTranslation(TranslationKeys.EMPTY_MESSAGE)
    );
  }

  ngAfterContentInit() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _templateMap: Record<string, TemplateRef<any>> = {};

    this.templates().forEach((item) => {
      switch (item.getType()) {
        case "header":
          this.headerTemplate.set(item.template);
          break;

        case "empty":
          this.emptyMessageTemplate.set(item.template);
          break;

        case "footer":
          this.footerTemplate.set(item.template);
          break;

        case "loader":
          this.loaderTemplate.set(item.template);
          break;

        case "togglericon":
          this.togglerIconTemplate.set(item.template);
          break;

        case "checkboxicon":
          this.checkboxIconTemplate.set(item.template);
          break;

        case "loadingicon":
          this.loadingIconTemplate.set(item.template);
          break;

        default:
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          _templateMap[item.name as any] = item.template;
          break;
      }
    });

    this._templateMap.set(_templateMap);
  }

  private triggerUpdateSerializedValue = effect(() => {
    // On value changes
    this.value();

    this.updateSerializedValue();
  });

  // This is also called when expand/collapsing
  updateSerializedValue() {
    this.serializedValue.set(serializeNodes(undefined, this.value(), 0, true));
  }

  onNodeClick(event: Event, node: TreeNode<TData>) {
    const eventTarget = event.target as HTMLElement;
    if (
      hasClass(eventTarget, "p-tree-toggler") ||
      hasClass(eventTarget, "p-tree-toggler-icon")
    ) {
      return;
    } else if (this.selectionMode()) {
      if (node.selectable === false) {
        node.style = "--p-focus-ring-color: none;";
        return;
      } else {
        node.style = "--p-focus-ring-color: var(--primary-color)";
      }

      const index = this.findIndexInSelection(node);
      const selected = index >= 0;

      const {
        isSingleSelectionMode,
        isMultipleSelectionMode,
        isCheckboxSelectionMode,
        selection,
      } = this.selectionData();
      if (isCheckboxSelectionMode) {
        if (selected) {
          if (this.propagateSelectionDown()) {
            this.propagateCheckboxSelectionDown(node, false);
          } else {
            this.selection.set(selection.filter((_, i) => i !== index));
          }

          if (this.propagateSelectionUp() && node.parent) {
            this.propagateCheckboxSelectionUp(node.parent, false);
          }

          const { selection: newSelection } = this.selectionData();
          this.selectionChange.emit(newSelection);
          this.onNodeUnselect.emit({ originalEvent: event, node: node });
        } else {
          if (this.propagateSelectionDown()) {
            this.propagateCheckboxSelectionDown(node, true);
          } else {
            this.selection.set([...selection, node]);
          }

          if (this.propagateSelectionUp() && node.parent) {
            this.propagateCheckboxSelectionUp(node.parent, true);
          }

          const { selection: newSelection } = this.selectionData();
          this.selectionChange.emit(newSelection);
          this.onNodeSelect.emit({ originalEvent: event, node: node });
        }
      } else {
        const metaSelection = this.metaKeySelection();

        if (metaSelection) {
          const metaKey =
            (event as KeyboardEvent).metaKey ||
            (event as KeyboardEvent).ctrlKey;
          const shiftKey = (event as KeyboardEvent).shiftKey;

          // Handle shift+click for range selection (only in multiple selection mode)
          if (shiftKey && isMultipleSelectionMode) {
            const anchor = this.selectionAnchor();
            if (anchor) {
              const rangeNodes = getNodesBetween(this.value(), anchor, node);
              if (rangeNodes !== undefined) {
                this.selection.set(rangeNodes);

                const { selection: newSelection } = this.selectionData();
                this.selectionChange.emit(newSelection);
                this.onNodeSelect.emit({ originalEvent: event, node: node });
              } else {
                // Fallback: treat as regular click if anchor not found
                this.selection.set([node]);
                this.selectionAnchor.set(node);

                const { selection: newSelection } = this.selectionData();
                this.selectionChange.emit(newSelection);
                this.onNodeSelect.emit({ originalEvent: event, node: node });
              }
            } else {
              // No anchor set, treat as regular click
              this.selection.set([node]);
              this.selectionAnchor.set(node);

              const { selection: newSelection } = this.selectionData();
              this.selectionChange.emit(newSelection);
              this.onNodeSelect.emit({ originalEvent: event, node: node });
            }
          } else if (selected && metaKey) {
            if (isSingleSelectionMode) {
              this.selectionChange.emit(null);
              this.selectionAnchor.set(null);
            } else {
              this.selection.set(selection.filter((_, i) => i !== index));

              const { selection: newSelection } = this.selectionData();
              this.selectionChange.emit(newSelection);
              // Don't update anchor on unselect
            }

            this.onNodeUnselect.emit({ originalEvent: event, node: node });
          } else {
            if (isSingleSelectionMode) {
              this.selectionChange.emit(node);
              this.selectionAnchor.set(node);
            } else if (isMultipleSelectionMode) {
              if (!metaKey && !this.nodeTouched) {
                this.selection.set([node]);
                this.selectionAnchor.set(node);
              } else {
                if (this.nodeTouched && metaKey) {
                  this.selection.set([...selection, node]);
                  this.selectionAnchor.set(node);
                } else if (
                  this.nodeTouched &&
                  this.touchSelectionMode() !== "multiple"
                ) {
                  this.selection.set([node]);
                  this.selectionAnchor.set(node);
                } else {
                  this.selection.set([...selection, node]);
                  this.selectionAnchor.set(node);
                }
              }

              const { selection: newSelection } = this.selectionData();
              this.selectionChange.emit(newSelection);
            }

            this.onNodeSelect.emit({ originalEvent: event, node: node });
          }
        } else {
          if (isSingleSelectionMode) {
            if (selected) {
              this.selection.set(undefined);
              this.selectionAnchor.set(null);
              this.onNodeUnselect.emit({ originalEvent: event, node: node });
            } else {
              this.selection.set(node);
              this.selectionAnchor.set(node);
              setTimeout(() => {
                this.onNodeSelect.emit({ originalEvent: event, node: node });
              });
            }
          } else {
            if (selected) {
              this.selection.set(selection.filter((_, i) => i !== index));
              // Don't update anchor on unselect
              this.onNodeUnselect.emit({ originalEvent: event, node: node });
            } else {
              if (
                this.nodeTouched &&
                this.touchSelectionMode() !== "multiple"
              ) {
                this.selection.set([node]);
              } else {
                this.selection.set([...selection, node]);
              }
              this.selectionAnchor.set(node);
              setTimeout(() => {
                this.onNodeSelect.emit({ originalEvent: event, node: node });
              });
            }
          }

          const { selection: newSelection } = this.selectionData();
          this.selectionChange.emit(newSelection);
        }
      }
    }

    this.nodeTouched = false;
  }

  onNodeTouchEnd() {
    this.nodeTouched = true;
  }

  onNodeRightClick(event: MouseEvent, node: TreeNode<TData>) {
    const contextMenu = this.contextMenu();
    if (contextMenu) {
      const eventTarget = event.target as Element;
      const className = eventTarget.getAttribute("class");

      if (className?.includes("p-tree-toggler")) {
        return;
      } else {
        const index = this.findIndexInSelection(node);
        const selected = index >= 0;

        if (!selected) {
          const { isSingleSelectionMode } = this.selectionData();
          if (isSingleSelectionMode) {
            this.selectionChange.emit(node);
          } else {
            this.selectionChange.emit([node]);
          }
        }

        contextMenu.show(event);
        this.onNodeContextMenuSelect.emit({ originalEvent: event, node: node });
      }
    }
  }

  findIndexInSelection(node: TreeNode<TData>) {
    const { selection, selectionArray } = this.selectionData();
    if (this.selectionMode() && selection) {
      return selectionArray.findIndex(
        (selectedNode) =>
          selectedNode === node ||
          (selectedNode.key === node.key && selectedNode.key !== undefined),
      );
    }
    return -1;
  }

  propagateCheckboxSelectionUp(node: TreeNode<TData>, select: boolean) {
    const { isCheckboxSelectionMode, selection } = this.selectionData();
    invariant(isCheckboxSelectionMode);

    if (node.children?.length) {
      let selectedCount: number = 0;
      let childPartialSelected: boolean = false;
      for (const child of node.children) {
        if (this.isSelected(child)) {
          selectedCount++;
        } else if (child.partialSelected) {
          childPartialSelected = true;
        }
      }

      if (select && selectedCount == node.children.length) {
        this.selection.set([...selection, node]);
        node.partialSelected = false;
      } else {
        if (!select) {
          const index = this.findIndexInSelection(node);
          if (index >= 0) {
            this.selection.set(selection.filter((_, i) => i != index));
          }
        }

        if (
          childPartialSelected ||
          (selectedCount > 0 && selectedCount != node.children.length)
        ) {
          node.partialSelected = true;
        } else {
          node.partialSelected = false;
        }
      }
    }

    const parent = node.parent;
    if (parent) {
      this.propagateCheckboxSelectionUp(parent, select);
    }
  }

  propagateCheckboxSelectionDown(node: TreeNode<TData>, select: boolean) {
    const { isCheckboxSelectionMode, selection } = this.selectionData();
    invariant(isCheckboxSelectionMode);

    const index = this.findIndexInSelection(node);
    if (select && index == -1 && node.selectable !== false) {
      this.selection.set([...selection, this.filterUnselectableChildren(node)]);
    } else if (!select && index > -1) {
      this.selection.set(selection.filter((_, i) => i !== index));
    }

    node.partialSelected = false;
    if (node.children?.length) {
      for (const child of node.children) {
        this.propagateCheckboxSelectionDown(child, select);
      }
    }
  }

  filterUnselectableChildren(node: TreeNode<TData>): TreeNode<TData> {
    const clonedNode = Object.assign({}, node);

    if (clonedNode.children?.length) {
      for (let child of clonedNode.children) {
        if (child.selectable === false) {
          clonedNode.children = clonedNode.children.filter(
            (val: TreeNode<TData>) => val !== child,
          );
        }
        child = this.filterUnselectableChildren(child);
      }
    }

    return clonedNode;
  }

  private isSelected(node: TreeNode<TData>) {
    return this.findIndexInSelection(node) != -1;
  }

  readonly selectionData = computed(() => {
    const selection = this.selection();

    const selectionMode = this.selectionMode();
    switch (selectionMode) {
      case "single": {
        invariant(!selection || !Array.isArray(selection));
        return {
          selectionMode,
          isSingleSelectionMode: true,
          isMultipleSelectionMode: false,
          isCheckboxSelectionMode: false,
          selection: (selection ?? null) as TreeNode<TData> | null,
          selectionArray: selection
            ? ([selection as TreeNode<TData>] as const)
            : ([] as const),
        } as const satisfies {
          selectionMode: "single";
          isSingleSelectionMode: true;
          isMultipleSelectionMode: false;
          isCheckboxSelectionMode: false;
          selection: TreeNode<TData> | null;
          selectionArray: TreeNode<TData>[];
        };
      }

      case "multiple": {
        invariant(!selection || Array.isArray(selection));
        return {
          selectionMode,
          isSingleSelectionMode: false,
          isMultipleSelectionMode: true,
          isCheckboxSelectionMode: false,
          selection: (selection ?? []) as TreeNode<TData>[],
          selectionArray: (selection ?? []) as TreeNode<TData>[],
        } as const satisfies {
          selectionMode: "multiple";
          isSingleSelectionMode: false;
          isMultipleSelectionMode: true;
          isCheckboxSelectionMode: false;
          selection: TreeNode<TData>[];
          selectionArray: TreeNode<TData>[];
        };
      }

      case "checkbox": {
        invariant(!selection || Array.isArray(selection));
        return {
          selectionMode,
          isSingleSelectionMode: false,
          isMultipleSelectionMode: false,
          isCheckboxSelectionMode: true,
          selection: (selection ?? []) as TreeNode<TData>[],
          selectionArray: (selection ?? []) as TreeNode<TData>[],
        } as const satisfies {
          selectionMode: "checkbox";
          isSingleSelectionMode: false;
          isMultipleSelectionMode: false;
          isCheckboxSelectionMode: true;
          selection: TreeNode<TData>[];
          selectionArray: TreeNode<TData>[];
        };
      }

      case undefined: {
        return {
          selectionMode: undefined,
          isSingleSelectionMode: false,
          isMultipleSelectionMode: false,
          isCheckboxSelectionMode: false,
          selection: [],
          selectionArray: [] as const,
        } as const satisfies {
          selectionMode: undefined;
          isSingleSelectionMode: false;
          isMultipleSelectionMode: false;
          isCheckboxSelectionMode: false;
          selection: TreeNode<TData>[];
          selectionArray: TreeNode<TData>[];
        };
      }

      default: {
        // new TS to check for exhaustiveness
        const _: never = selectionMode;
        throw new Error();
      }
    }
  });

  onDragOver(event: DragEvent) {
    if (this.droppableNodes() && this.value().length === 0) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }

      event.preventDefault();
    }
  }

  onDrop(event: DragEvent) {
    if (this.droppableNodes() && this.value().length === 0) {
      event.preventDefault();
      const dragNode = this.dragNode;

      if (this.allowDrop(dragNode, null, this.dragNodeScope)) {
        const dragNodeIndex = this.dragNodeIndex;
        invariant(
          dragNodeIndex !== null &&
            dragNodeIndex !== undefined &&
            dragNodeIndex >= 0,
        );
        this.value.set(this.value());

        if (this.validateDrop()) {
          this.onNodeDrop.emit({
            originalEvent: event,
            dragNode: dragNode,
            dropNode: null,
            index: dragNodeIndex,
            accept: () => {
              this.processTreeDrop(dragNode, dragNodeIndex);
            },
          });
        } else {
          this.onNodeDrop.emit({
            originalEvent: event,
            dragNode: dragNode,
            dropNode: null,
            index: dragNodeIndex,
          });

          this.processTreeDrop(dragNode, dragNodeIndex);
        }
      }
    }
  }

  processTreeDrop(
    dragNode: TreeNode<TData> | null | undefined,
    dragNodeIndex: number,
  ) {
    invariant(this.dragDropService);
    invariant(this.dragNodeSubNodes);
    invariant(dragNode);
    this.dragNodeSubNodes.splice(dragNodeIndex, 1);
    this.value().push(dragNode!);
    this.dragDropService.stopDrag({
      node: dragNode!,
    });
  }

  onDragEnter() {
    if (
      this.droppableNodes() &&
      this.allowDrop(this.dragNode, null, this.dragNodeScope)
    ) {
      this.dragHover.set(true);
    }
  }

  onDragLeave(event: DragEvent) {
    if (this.droppableNodes()) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      if (
        event.x > rect.left + rect.width ||
        event.x < rect.left ||
        event.y > rect.top + rect.height ||
        event.y < rect.top
      ) {
        this.dragHover.set(false);
      }
    }
  }

  allowDrop(
    dragNode: TreeNode<TData> | undefined | null,
    dropNode: TreeNode<TData> | null,
    dragNodeScope: unknown,
    dropPoint: "node" | "between" = "node",
  ): boolean {
    if (!dragNode) {
      //prevent random html elements to be dragged
      return false;
    }

    if (!this.isValidDragScope(dragNodeScope)) {
      return false;
    }

    if (dropNode) {
      if (dragNode === dropNode) {
        return false;
      } else {
        let parent = dropNode.parent;
        while (parent != null) {
          if (parent === dragNode) {
            return false;
          }

          parent = parent.parent;
        }
      }
    }

    return true;
  }

  private isValidDragScope(dragScope: unknown): boolean {
    const dropScope = this.droppableScope();
    if (!dropScope) {
      return true;
    }

    if (typeof dropScope === "string") {
      if (typeof dragScope === "string") {
        return dropScope === dragScope;
      } else if (Array.isArray(dragScope)) {
        return dragScope.includes(dropScope);
      }
    } else if (Array.isArray(dropScope)) {
      if (typeof dragScope === "string") {
        return dropScope.includes(dragScope);
      } else if (Array.isArray(dragScope)) {
        for (const s of dropScope) {
          for (const ds of dragScope) {
            if (s === ds) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Scrolls to virtual index.
   * @param {number} index - Index to be scrolled.
   * @group Method
   */
  public scrollToVirtualIndex(index: number) {
    this.virtualScroll() && this.scroller()?.scrollToIndex(index, "smooth");
  }

  /**
   * Scrolls to virtual index.
   * @param {ScrollToOptions} options - Scroll options.
   * @group Method
   */
  public scrollTo(options: ScrollToOptions) {
    const wrapperViewChild = this.wrapperViewChild();

    if (this.virtualScroll()) {
      this.scroller()?.scrollTo(options);
    } else if (wrapperViewChild?.nativeElement) {
      wrapperViewChild.nativeElement.scrollTo(options);
    }
  }

  getBlockableElement(): HTMLElement {
    return this.el.nativeElement.children[0] as HTMLElement;
  }

  getVirtualItemSize(item: SerializedNode<TData>) {
    // child
    if (item.node.leaf || item.node.children === undefined) {
      const childItemSize = this.childItemSize();
      invariant(typeof childItemSize === "number");
      return childItemSize;
    }

    // parent
    const parentItemSize = this.parentItemSize();
    invariant(typeof parentItemSize === "number");
    return parentItemSize;
  }

  ngOnDestroy() {
    if (this.dragStartSubscription) {
      this.dragStartSubscription.unsubscribe();
    }

    if (this.dragStopSubscription) {
      this.dragStopSubscription.unsubscribe();
    }
  }
}

@NgModule({
  imports: [TreeComponent, TreeNodeComponent],
  exports: [TreeComponent, SharedModule, ScrollerModule],
})
export class TreeModule {}
